import Enumerable from "linq";
import { RecipientTypeEnum } from "../../../../common/enums/RecipientTypeEnum";
import { IAlarmNotificationObjectCountItemModel, IAlarmNotificationObjectCountModel } from "../../../../services/alarm-data-service/Models";
import { Options } from "highcharts";
import { IeLynxObject } from "../../../../common/models/eLynxObject";
import { ActionMenuItem, BaseActions } from "../../../action-menu/ActionMenuItem";
import i18n from "../../../../services/localization-service/i18n";

export const chartColors = [
    "#2ac8b4",
    "#767676",
    "#fe6d73",
    "#fee890",
    "#227c9d"
];


export enum NotificationDetailActionEnum  {
    Edit = 'edit',
    Reset = 'reset'
}

export type NotificationDetailActionItem = {
    action: NotificationDetailActionEnum | BaseActions | string
} & ActionMenuItem;

export const notificationDetailActionItems: NotificationDetailActionItem[] = [{
    label: i18n.t("buttons.reset"),
    //icon: () => <FontAwesomeIcon icon={faSquarePlus} size="1x" />,
    action: NotificationDetailActionEnum.Reset,
    color: "primary.main",
    order: 0
}];



export interface IChartCountModel {
    chartOptions: Options;
    elynxObject: IeLynxObject;
    parentObject: IeLynxObject;
    isMenuVisible: boolean;
    isMenuPopupVisible: boolean;
    selected: boolean;
    textCount: number;
    emailCount: number;
    voiceCount: number;
    totalCount: number;
}


function numberWithCommas(x: number) {
    return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}


function _getChartOptions(
        emailCount: number,
        textCount: number,
        voiceCount: number): Options {
    
        const highChartsOptions: Options =
        {
            chart: {
                animation: false,
                type: "bar",
                backgroundColor: "transparent",
                borderWidth: 0,
                height: 45, //50,
                margin: [0, 0, 0, 0],
                spacing: [0, 0, 0, 0],
                style: {
                    font: "inherit",
                    overflow: 'visible'
                },
                reflow: true,
                // small optimalization, saves 1-2 ms each sparkline
                //skipClone: true
            },
            credits: {
                enabled: false
            },
            exporting: {
                enabled: false
            },
            legend: {
                enabled: false
            },
            plotOptions: {
                bar: {
                    dataLabels: {
                        enabled: false
                    },
                    //pointWidth: 18,
                    //pointPadding: 0,
                    //groupPadding: 0,
                    //borderWidth: 0,
                    //shadow: false
                },
                series: {
                    stacking: "normal",
                    states: {
                        //inactive: {
                        //    opacity: 1
                        //},
                        hover: {
                            enabled: false
                        }
                    }
                },
            },
            series: [{
                animation: false,
                type: 'bar',
                color: chartColors[0],
                name: 'Text',
                data: [textCount],
                //marker:
                //{
                //    enabled: false,
                //    radius: 1
                //},
                shadow: false,
                states: {
                    hover: {
                        lineWidth: 1,
                        halo: {
                            size: 0
                        }
                    }
                },
                yAxis: 0.0
            }, {
                animation: false,
                type: 'bar',
                color: chartColors[1],
                name: 'Email',
                data: [emailCount],
                //marker:
                //{
                //    enabled: false,
                //    radius: 1
                //},
                shadow: false,
                states: {
                    hover: {
                        lineWidth: 1,
                        halo: {
                            size: 0
                        }
                    }
                },
                yAxis: 0.0
            }, {
                animation: false,
                type: 'bar',
                color: chartColors[2],
                name: 'Voice',
                data: [voiceCount],
                //marker:
                //{
                //    enabled: false,
                //    radius: 1
                //},
                shadow: false,
                states: {
                    hover: {
                        lineWidth: 1,
                        halo: {
                            size: 0
                        }
                    }
                },
                yAxis: 0.0
            }],
            title: {
                text: undefined
            },
            subtitle: {
                text: undefined
            },
            tooltip: {
                hideDelay: 1,
                outside: true,
                formatter: function () {
                    return `${numberWithCommas(this.y as number)} ${this.series.options.name} Notifications`;
                },
                backgroundColor: "rgba(255,255,255,1)" as any,
                style: {
                    fontSize: ".95em",
                    color: "#536580",
                    opacity: 1,
                    backgroundColor: "rgba(255,255,255,1)",
                    zIndex: 9999999
                }//,
                //useHTML: true
            },
            xAxis:
                [
                    {
                        labels: {
                            enabled: false
                        },
                        startOnTick: false,
                        endOnTick: false,
                        //min: startDateMS,
                        //max: endDateMS,
                        minPadding: 0,
                        maxPadding: 0,
                        tickPositions: [],
                        title: undefined,
                        //type: "datetime",
                        visible: false
                    }
                ],
            yAxis: [
                {
                    labels: {
                        enabled: false
                    },
                    startOnTick: false,
                    endOnTick: false,
                    minPadding: 0,
                    maxPadding: 0,
                    tickPositions: [0],
                    title: undefined,
                    type: "linear",
                    visible: false
                }
            ]
        };
    
        return highChartsOptions;
    }    




const NotificationsDetailChartUtils = {


    getChartData(notificationCountData: IAlarmNotificationObjectCountModel,
        showText: boolean,
        showEmail: boolean,
        showVoice: boolean,
        sortExpression: string,
        sortAscending: boolean
        ) : IChartCountModel[]  {

        const data = showText && showEmail && showVoice ?
            notificationCountData.objectCountItems :
            notificationCountData.objectCountItems.filter(o => (o.recipientType === RecipientTypeEnum.Text && showText) ||
                (o.recipientType === RecipientTypeEnum.Voice && showVoice) ||
                (o.recipientType === RecipientTypeEnum.Email && showEmail));
    
        let query = Enumerable.from(data)
            .groupBy(o => o.eLynxObject.objectName)
            .select(o => {
    
                const countModel: IAlarmNotificationObjectCountItemModel = o.first();
    
                const textCount: number = o.where(x => x.recipientType === RecipientTypeEnum.Text).sum(x => x.currentTimeframeCount);
                const emailCount: number = o.where(x => x.recipientType === RecipientTypeEnum.Email).sum(x => x.currentTimeframeCount);
                const voiceCount: number = o.where(x => x.recipientType === RecipientTypeEnum.Voice).sum(x => x.currentTimeframeCount);
                const totalCount: number = o.sum(x => x.currentTimeframeCount);
    
                const item: IChartCountModel = {
                    chartOptions: _getChartOptions(emailCount, textCount, voiceCount),
                    elynxObject: countModel.eLynxObject,
                    parentObject: countModel.parentObject,
                    isMenuVisible: notificationCountData.hasEdit,
                    isMenuPopupVisible: false,
                    selected: false,
                    textCount: textCount,
                    emailCount: emailCount,
                    voiceCount: voiceCount,
                    totalCount: totalCount
                };
    
                return item;
            });
    
        if (sortExpression === "Text") {
            if (sortAscending) {
                query = query.orderBy(o => o.textCount)
                    .thenBy(o => o.elynxObject.objectName)
    
            } else {
                query = query.orderByDescending(o => o.textCount)
                    .thenBy(o => o.elynxObject.objectName)
            }
        } else if (sortExpression === "Email") {
            if (sortAscending) {
                query = query.orderBy(o => o.emailCount)
                    .thenBy(o => o.elynxObject.objectName)
    
            } else {
                query = query.orderByDescending(o => o.emailCount)
                    .thenBy(o => o.elynxObject.objectName)
            }
        } else if (sortExpression === "Voice") {
            if (sortAscending) {
                query = query.orderBy(o => o.voiceCount)
                    .thenBy(o => o.elynxObject.objectName)
    
            } else {
                query = query.orderByDescending(o => o.voiceCount)
                    .thenBy(o => o.elynxObject.objectName)
            }
        } else if (sortExpression === "Total") {
            if (sortAscending) {
                query = query.orderBy(o => o.totalCount)
                    .thenBy(o => o.elynxObject.objectName)
    
            } else {
                query = query.orderByDescending(o => o.totalCount)
                    .thenBy(o => o.elynxObject.objectName)
            }
        }
    
        const chartData = query.toArray();
    
        //// if there is not enough data to fill the chart, add blank items
        //// so the bars render more closley than the default highcharts layout
        //if (chartData.length > 0 && chartData.length < this._minChartItems) {
        //    const count = this._minChartItems - chartData.length;
        //    for (let i = 0; i < count; ++i) {
        //        const item: IChartCountModel = {
        //            objectName: "",
        //            textCount: 0,
        //            emailCount: 0,
        //            voiceCount: 0,
        //            totalCount: 0
        //        };
    
        //        chartData.push(item);
        //    }
        //}
    
        return chartData;
    
    }

    // private _getChartData(notificationCountData: Models.IAlarmNotificationObjectCountModel): IChartCountModel[] {

    //     const data = this.showText && this.showEmail && this.showVoice && this.allowVoiceAlarm ?
    //         notificationCountData.objectCountItems :
    //         notificationCountData.objectCountItems.filter(o => (o.recipientType === Enums.RecipientTypeEnum.Text && this.showText) ||
    //             (o.recipientType === Enums.RecipientTypeEnum.Voice && this.showVoice && this.allowVoiceAlarm) ||
    //             (o.recipientType === Enums.RecipientTypeEnum.Email && this.showEmail));
    
    //     let query = Enumerable.from(data)
    //         .groupBy(o => o.eLynxObject.objectName)
    //         .select(o => {
    
    //             const countModel: Models.IAlarmNotificationObjectCountItemModel = o.firstOrDefault();
    
    //             const textCount: number = o.where(x => x.recipientType === Enums.RecipientTypeEnum.Text).sum(x => x.currentTimeframeCount);
    //             const emailCount: number = o.where(x => x.recipientType === Enums.RecipientTypeEnum.Email).sum(x => x.currentTimeframeCount);
    //             const voiceCount: number = o.where(x => x.recipientType === Enums.RecipientTypeEnum.Voice).sum(x => x.currentTimeframeCount);
    //             const totalCount: number = o.sum(x => x.currentTimeframeCount);
    
    //             const item: IChartCountModel = {
    //                 chartOptions: this._getChartOptions(emailCount, textCount, voiceCount),
    //                 elynxObject: countModel.eLynxObject,
    //                 parentObject: countModel.parentObject,
    //                 isMenuVisible: notificationCountData.hasEdit,
    //                 isMenuPopupVisible: false,
    //                 selected: false,
    //                 textCount: textCount,
    //                 emailCount: emailCount,
    //                 voiceCount: voiceCount,
    //                 totalCount: totalCount
    //             };
    
    //             return item;
    //         });
    
    //     if (this.sortExpression === "Text") {
    //         if (this.sortAscending) {
    //             query = query.orderBy(o => o.textCount)
    //                 .thenBy(o => o.elynxObject.objectName)
    
    //         } else {
    //             query = query.orderByDescending(o => o.textCount)
    //                 .thenBy(o => o.elynxObject.objectName)
    //         }
    //     } else if (this.sortExpression === "Email") {
    //         if (this.sortAscending) {
    //             query = query.orderBy(o => o.emailCount)
    //                 .thenBy(o => o.elynxObject.objectName)
    
    //         } else {
    //             query = query.orderByDescending(o => o.emailCount)
    //                 .thenBy(o => o.elynxObject.objectName)
    //         }
    //     } else if (this.sortExpression === "Voice") {
    //         if (this.sortAscending) {
    //             query = query.orderBy(o => o.voiceCount)
    //                 .thenBy(o => o.elynxObject.objectName)
    
    //         } else {
    //             query = query.orderByDescending(o => o.voiceCount)
    //                 .thenBy(o => o.elynxObject.objectName)
    //         }
    //     } else if (this.sortExpression === "Total") {
    //         if (this.sortAscending) {
    //             query = query.orderBy(o => o.totalCount)
    //                 .thenBy(o => o.elynxObject.objectName)
    
    //         } else {
    //             query = query.orderByDescending(o => o.totalCount)
    //                 .thenBy(o => o.elynxObject.objectName)
    //         }
    //     }
    
    //     const chartData = query.toArray();
    
    //     //// if there is not enough data to fill the chart, add blank items
    //     //// so the bars render more closley than the default highcharts layout
    //     //if (chartData.length > 0 && chartData.length < this._minChartItems) {
    //     //    const count = this._minChartItems - chartData.length;
    //     //    for (let i = 0; i < count; ++i) {
    //     //        const item: IChartCountModel = {
    //     //            objectName: "",
    //     //            textCount: 0,
    //     //            emailCount: 0,
    //     //            voiceCount: 0,
    //     //            totalCount: 0
    //     //        };
    
    //     //        chartData.push(item);
    //     //    }
    //     //}
    
    //     return chartData;
    // }



    // private _getChartOptions(
    //     emailCount: number,
    //     textCount: number,
    //     voiceCount: number): HighchartsOptions {
    
    //     const highChartsOptions: HighchartsOptions =
    //     {
    //         chart: {
    //             animation: false,
    //             type: "bar",
    //             backgroundColor: "transparent",
    //             borderWidth: 0,
    //             height: 45, //50,
    //             margin: [0, 0, 0, 0],
    //             spacing: [0, 0, 0, 0],
    //             style: {
    //                 font: "inherit",
    //                 overflow: 'visible'
    //             },
    //             reflow: true,
    //             // small optimalization, saves 1-2 ms each sparkline
    //             skipClone: true
    //         },
    //         credits: {
    //             enabled: false
    //         },
    //         exporting: {
    //             enabled: false
    //         },
    //         legend: {
    //             enabled: false
    //         },
    //         plotOptions: {
    //             bar: {
    //                 dataLabels: {
    //                     enabled: false
    //                 },
    //                 //pointWidth: 18,
    //                 //pointPadding: 0,
    //                 //groupPadding: 0,
    //                 //borderWidth: 0,
    //                 //shadow: false
    //             },
    //             series: {
    //                 stacking: "normal",
    //                 states: {
    //                     //inactive: {
    //                     //    opacity: 1
    //                     //},
    //                     hover: {
    //                         enabled: false
    //                     }
    //                 }
    //             },
    //         },
    //         series: [{
    //             animation: false,
    //             color: this._chartColors[0],
    //             name: 'Text',
    //             data: [textCount],
    //             marker:
    //             {
    //                 enabled: false,
    //                 radius: 1
    //             },
    //             shadow: false,
    //             states: {
    //                 hover: {
    //                     lineWidth: 1,
    //                     halo: {
    //                         size: 0
    //                     }
    //                 }
    //             },
    //             yAxis: 0.0
    //         }, {
    //             animation: false,
    //             color: this._chartColors[1],
    //             name: 'Email',
    //             data: [emailCount],
    //             marker:
    //             {
    //                 enabled: false,
    //                 radius: 1
    //             },
    //             shadow: false,
    //             states: {
    //                 hover: {
    //                     lineWidth: 1,
    //                     halo: {
    //                         size: 0
    //                     }
    //                 }
    //             },
    //             yAxis: 0.0
    //         }, {
    //             animation: false,
    //             color: this._chartColors[2],
    //             name: 'Voice',
    //             data: [voiceCount],
    //             marker:
    //             {
    //                 enabled: false,
    //                 radius: 1
    //             },
    //             shadow: false,
    //             states: {
    //                 hover: {
    //                     lineWidth: 1,
    //                     halo: {
    //                         size: 0
    //                     }
    //                 }
    //             },
    //             yAxis: 0.0
    //         }],
    //         title: {
    //             text: null
    //         },
    //         subtitle: {
    //             text: null
    //         },
    //         tooltip: {
    //             hideDelay: 1,
    //             outside: true,
    //             formatter: function () {
    //                 return `${Utilities.numberWithCommas(this.y)} ${this.series.options.name} Notifications`;
    //             },
    //             backgroundColor: "rgba(255,255,255,1)" as any,
    //             style: {
    //                 fontSize: ".95em",
    //                 color: "#536580",
    //                 opacity: 1,
    //                 backgroundColor: "rgba(255,255,255,1)",
    //                 zIndex: 9999999
    //             }//,
    //             //useHTML: true
    //         },
    //         xAxis:
    //             [
    //                 {
    //                     labels: {
    //                         enabled: false
    //                     },
    //                     startOnTick: false,
    //                     endOnTick: false,
    //                     //min: startDateMS,
    //                     //max: endDateMS,
    //                     minPadding: 0,
    //                     maxPadding: 0,
    //                     tickPositions: [],
    //                     title: null,
    //                     //type: "datetime",
    //                     visible: false
    //                 }
    //             ],
    //         yAxis: [
    //             {
    //                 labels: {
    //                     enabled: false
    //                 },
    //                 startOnTick: false,
    //                 endOnTick: false,
    //                 minPadding: 0,
    //                 maxPadding: 0,
    //                 tickPositions: [0],
    //                 title: null,
    //                 type: "linear",
    //                 visible: false
    //             }
    //         ]
    //     };
    
    //     return highChartsOptions;
    // }    




    // getChartOptions: function(
    //     durationDays: number,
    //     notificationCounts: IAlarmNotificationCountModel[],
    //     showText: boolean,
    //     showEmail: boolean,
    //     showVoice: boolean,
    // ): GraphItemOptions {


    //     const data = showText && showEmail && showVoice ?
    //                     notificationCounts :
    //                     notificationCounts.filter(o => (o.recipientType === RecipientTypeEnum.Text && showText) ||
    //                                                     (o.recipientType === RecipientTypeEnum.Voice && showVoice) ||
    //                                                     (o.recipientType === RecipientTypeEnum.Email && showEmail));
    
    //     let groupedData = Enumerable.from(data)
    //                     .where(o => o.recipientType === RecipientTypeEnum.Text)
    //                     .groupBy(o => new Date(o.alarmTimestamp));


    // let shapedData = groupedData
    //     .select(o => {
    //         const item = {
    //             alarmTimestamp: o.key(),
    //             count: o.sum(x => x.count)
    //         };

    //         return item;
    //     })
    //     .toArray();

    // const textSeriesData = Enumerable.from(shapedData).select(o => {
    //     return [o.alarmTimestamp.getTime(), o.count]
    // }).toArray();


    // groupedData = Enumerable.from(data)
    //     .where(o => o.recipientType === RecipientTypeEnum.Email)
    //     .groupBy(o => new Date(o.alarmTimestamp));


    // shapedData = groupedData
    //     .select(o => {
    //         const item = {
    //             alarmTimestamp: o.key(),
    //             count: o.sum(x => x.count)
    //         };

    //         return item;
    //     })
    //     .toArray();

    // const emailSeriesData = Enumerable.from(shapedData).select(o => {
    //     return [o.alarmTimestamp.getTime(), o.count]
    // }).toArray();

    // groupedData = Enumerable.from(data)
    //     .where(o => o.recipientType === RecipientTypeEnum.Voice)
    //     .groupBy(o => new Date(o.alarmTimestamp));


    // shapedData = groupedData
    //     .select(o => {
    //         const item = {
    //             alarmTimestamp: o.key(),
    //             count: o.sum(x => x.count)
    //         };

    //         return item;
    //     })
    //     .toArray();

    // const voiceSeriesData = Enumerable.from(shapedData).select(o => {
    //     return [o.alarmTimestamp.getTime(), o.count]
    // }).toArray();


    // const pill = 'display: inline-block;width: 1.3rem; text-align: center; font-size: .7rem; xcolor: #fff; border: 1px solid #536580; cursor: auto; height: .7rem; line-height: .8rem; vertical-align: middle; border-radius: 8px;';

    // const seriesData: SeriesOptionsType[] = [{

    //     name: `<span><span style="${pill}background-color:${chartColors[0]}; border-color:${chartColors[0]};"></span> Text</span>`,
    //     type: 'line',
    //     data: textSeriesData,
    //     color: chartColors[0],
    // }, {
    //         name: `<span><span style="${pill}background-color:${chartColors[1]}; border-color:${chartColors[1]};"></span> Email</span>`,
    //         type: 'line',
    //         data: emailSeriesData,
    //         color: chartColors[1],
    // }];

    // if (showVoice) {

    //     seriesData.push({
    //         name: `<span><span style="${pill}background-color:${chartColors[2]}; border-color:${chartColors[2]};"></span> Voice</span>`,
    //         type: 'line',
    //         data: voiceSeriesData,
    //         color: chartColors[2]
    //     });
    // }

    // const options: Options = {
    //     chart: {
    //         type: 'line',
    //         zooming: {
    //             type: 'x'
    //         }

    //     },
    //     credits: {
    //         enabled: false
    //     },
    //     exporting: {
    //         enabled: false
    //     },
    //     title: {
    //         text: 'NOTIFICATIONS BY TYPE',
    //         align: "left",
    //         style: {
    //             fontSize: ".95rem"
    //         }
    //     },
    //     xAxis: [{
    //         type: "datetime",
    //         labels: {
    //             useHTML: true,
    //             formatter: function () {
    //                 return durationDays === 1 ? 
    //                             format(new Date(this.value), 'MM/dd KK:mm a') : 
    //                             format(new Date(this.value), 'MM/dd/y')
    //             },
    //             staggerLines: 2,
    //             overflow: "justify"
    //         },
    //         //tickInterval: durationDays === 1 ? undefined : 24 * 60
    //     }],
    //     yAxis: [{
    //         title: {
    //             text: 'Notifications'
    //         },
            
    //     }],
    //     tooltip: {
    //         formatter: function () {
    //             return `<b>${this.y}</b> Notifications<br />${format(new Date(this.x as number), 'LLL dd, KK:mm a')}`; 
    //         }
    //     },
    //     plotOptions: {
    //         area: {
    //             marker: {
    //                 enabled: false,
    //                 symbol: 'circle',
    //                 radius: 2,
    //                 states: {
    //                     hover: {
    //                         enabled: true
    //                     }
    //                 }
    //             }
    //         }
    //     },
    //     series: seriesData,
    //     legend: {
    //         enabled: true,
    //         useHTML: true,
    //         // hide the symbols since we have custom symbols to display
    //         symbolWidth: 0,
    //         symbolRadius: 0,
    //         itemStyle: {
    //             fontSize: ".8em",
    //             color: "#536580",
    //             fontWeight: "normal"
    //         }
    //     }
    // };
                                            
    //     return  {...options,
    //             headerTitle: "notification counts",
    //             id: GraphUtils.getChartID(),
    //             index: 0,
                
    //         }
    // }
}

export default NotificationsDetailChartUtils;